import Icon from "../../components/common/Icon";
import gestionDefunt from "../../img/v2_i_gestionDefunt.png";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import {
    checkRequiredFields,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput,
    fileToBase64
} from "../../helper/tools";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import { useState, useEffect, useRef } from "react";
import InputFullAddress from "../../components/common/input-layout/InputFullAddress";
import useAuth from "../../hooks/useAuth";
import jwt_decode from "jwt-decode";
import useData from "../../hooks/useData";
import { getDefaultImageLibrary } from "../../api/admin/imageReq";
import { createDeceased } from "../../api/pro/deceasedReq";
import { getAgencyDefaultDeceasedImage } from "../../api/pro/agencyReq";
import FormDeathInfos from "./FormDeathInfos";
import FormBirthInfos from "./FormBirthInfos";

export default function FormDeceasedInfos({ deceasedInfos, setDeceasedInfos, setShowModal }) {

    const { auth } = useAuth();
    const token = auth?.token;
    const decodedToken = jwt_decode(token);
    const agencyId = decodedToken?.agency;

    const [ fullForm, setFullForm ] = useState(deceasedInfos);
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const [selectedImage, setSelectedImage] = useState();
    const [showPortraitModal, setShowPortraitModal] = useState(false);
    const [selectedPortrait, setSelectedPortrait] = useState(null);
    const [selectedCompressedFile, setSelectedCompressedFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [combinedImageList, setCombinedImageList] = useState([]);
    const [useDefaultPortrait, setUseDefaultPortrait] = useState(true);
    const { imageLib, setImageLib, agencyDefaultDeceasedImage, setAgencyDefaultDeceasedImage } = useData({});
    const portraitSnapshotRef = useRef(null);

    const apiImageLib = async () => {
        const response = await getDefaultImageLibrary(token);
        setImageLib(response);
    };

    const apiDefaultImage = async () => {
        const response = await getAgencyDefaultDeceasedImage(token, agencyId)
        let data;
        if (Array.isArray(response)) {
            data = response.length > 0 ? response[0] : {};
        } else {
            data = response || {};
        }

        if (data && !data.defaultImage) {
            if (data.image) {
                data = { ...data, defaultImage: data.image };
            } else if (data.default_image) {
                data = { ...data, defaultImage: data.default_image };
            }
        }

        setAgencyDefaultDeceasedImage(data);
    };

    // Bloquer le scroll du body quand la modal est ouverte
    useEffect(() => {
        if (showPortraitModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        // Cleanup: retirer la classe quand le composant est démonté
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showPortraitModal]);

    useEffect(() => {
        apiImageLib();
        apiDefaultImage();
    }, [token, agencyId]);

    useEffect(() => {
        if (imageLib && imageLib.length > 0) {
            const enabledImages = imageLib.filter(image => image?.enabled && image?.category === 1);
            const combined = [];

            // Images de l'agence affichées en priorité, avant les images communes (admin).
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${agencyId}/${image?.url}`,
                    source: 'agency',
                    uniqueKey: `agency-${agencyId}-${image.id}`
                });
            });
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${image?.url}`,
                    source: 'root',
                    uniqueKey: `root-${image.id}`
                });
            });

            setCombinedImageList(combined);
        }
    }, [imageLib, agencyId, IMG_FOLDER]);

    useEffect(() => {
        if (!deceasedInfos?.portrait || deceasedInfos?.portrait === "") {
            setUseDefaultPortrait(true);
            // Si portrait est vide, afficher l'image par défaut de l'agence. isAgencySpecific
            // (calculé côté back via file_exists) indique le dossier réel, sans quoi une image
            // propre à l'agence (absente du dossier racine partagé) ne s'affichait pas.
            if (agencyDefaultDeceasedImage?.defaultImage) {
                setSelectedImage(agencyDefaultDeceasedImage?.isAgencySpecific
                    ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                    : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`);
            }
        } else {
            setUseDefaultPortrait(false);
            if (deceasedInfos?.useGalleryPortrait && deceasedInfos?.portrait) {
                setSelectedPortrait(deceasedInfos.portrait);
                const agencyImagePath = `${IMG_FOLDER}/img/deceased_library/${agencyId}/${deceasedInfos.portrait}`;
                const rootImagePath = `${IMG_FOLDER}/img/deceased_library/${deceasedInfos.portrait}`;

                const img = new Image();
                img.onload = () => {
                    setSelectedImage(agencyImagePath);
                };
                img.onerror = () => {
                    setSelectedImage(rootImagePath);
                };
                img.src = agencyImagePath;
            } else if (deceasedInfos?.portrait && deceasedInfos?.uploadDir) {
                setSelectedImage(`${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${deceasedInfos.portrait}`);
            }
        }
    }, [deceasedInfos, agencyId, IMG_FOLDER, agencyDefaultDeceasedImage]);

    const handleFileChange = async (e) => {
        let selectedFile = e.target.files[0];
        if (e && selectedFile) {
            setFileName(selectedFile.name);
            
            try {
                const base64Photo = await fileToBase64(selectedFile);
                setSelectedCompressedFile(base64Photo);
                setSelectedImage(base64Photo);
                setSelectedPortrait(null);
                setUseDefaultPortrait(false); // Décocher la case

                setFullForm(prevForm => ({
                    ...prevForm,
                    portrait: selectedFile?.name,
                    url: "",
                    useGalleryPortrait: false,
                    file: base64Photo,
                    file_name: selectedFile.name
                }));
                
            } catch (error) {
                console.error("Erreur lors de la conversion en base64 : ", error);
            }
        }
    };

    const handleLibFileSelected = (url, imagePath = null) => {
        if (selectedPortrait === url) {
            setSelectedPortrait(null);
            setSelectedImage(null);
            setFullForm(prevForm => ({
                ...prevForm,
                url: "",
                useGalleryPortrait: false,
                portrait: ""
            }));
        } else {
            setSelectedPortrait(url);
            const displayPath = imagePath || `${IMG_FOLDER}/img/deceased_library/${url}`;
            setSelectedImage(displayPath);
            setSelectedCompressedFile(null);
            setFileName("");
            setUseDefaultPortrait(false); // Décocher la case
            setFullForm(prevForm => ({
                ...prevForm,
                url: url,
                useGalleryPortrait: true,
                portrait: url
            }));
        }
    };

    const handleCancelPortrait = () => {
        const snapshot = portraitSnapshotRef.current;
        if (snapshot) {
            setFullForm(prevForm => ({
                ...prevForm,
                portrait: snapshot.portrait,
                useGalleryPortrait: snapshot.useGalleryPortrait,
                url: snapshot.url,
                file: snapshot.file,
                file_name: snapshot.file_name,
            }));
            setSelectedImage(snapshot.selectedImage);
            setSelectedPortrait(snapshot.selectedPortrait);
            setSelectedCompressedFile(snapshot.selectedCompressedFile);
            setFileName(snapshot.fileName);
            setUseDefaultPortrait(snapshot.useDefaultPortrait);
        }
        setShowPortraitModal(false);
    };

    const onChangeActualPlace = (e) => {
        const fieldMap = {
            'actualZipcode': 'zipcode',
            'actualCity': 'city',
            'actualCountry': 'country',
            'address': 'address',
            'addressComp': 'addressComp'
        };
        
        const fieldKey = fieldMap[e.target.name] || e.target.name;
        
        setFullForm(prevForm => ({
            ...prevForm, 
            actualplace: { 
                ...prevForm?.actualplace, 
                [fieldKey]: e.target.value 
            } 
        }));
        validateFormInput(e);
    }

    const onChange = (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setFullForm(prevForm => ({
            ...prevForm,
            [e.target.name]: value
        }));
        validateFormInput(e);
    };

    const handlePublicationChange = (e) => {
        const value = parseInt(e.target.value);
        setFullForm(prevForm => ({
            ...prevForm,
            isListed: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!checkRequiredFields()) {
            return;
        }
        
        const formData = new FormData(e.target);
        const formValues = Object.fromEntries(formData.entries());

        let body = {
            deceasedId: deceasedInfos?.id,
            agencyId: agencyId,
            title: formValues?.title || fullForm?.title,
            civility: formValues?.civility || fullForm?.civility || "M",
            hiddenCivility: formValues?.hiddenCivility === "on" || fullForm?.hiddenCivility || false,
            lastName: (formValues?.lastName || fullForm.lastName)?.trim(),
            firstName: (formValues?.firstName || fullForm?.firstName)?.trim(),
            birthName: (formValues?.birthName || fullForm?.birthName)?.trim(),
            birthDate: fullForm?.birthDate || formValues?.birthDate,
            // fullForm.isListed est initialisé directement depuis deceasedInfos.isListed (booléen
            // natif possible), pas seulement 1/"1" mis à jour par le radio : sans le cas booléen,
            // soumettre sans re-cliquer "Oui" (déjà coché par défaut) repassait isListed à false.
            isListed: fullForm?.isListed === 1 || fullForm?.isListed === "1" || fullForm?.isListed === true,
            portrait: fullForm?.portrait || "",
            useGalleryPortrait: fullForm?.useGalleryPortrait || false,
            file: fullForm?.file || undefined,
            file_name: fullForm?.file_name || undefined,
            deathDate: fullForm?.deathDate || formValues?.deathDate,
            actualplace: {
                address: fullForm?.actualplace?.address || formValues?.address,
                addressComp: fullForm?.actualplace?.addressComp || formValues?.addressComp,
                zipcode: fullForm?.actualplace?.zipcode || formValues?.actualZipcode,
                city: fullForm?.actualplace?.city || formValues?.actualCity,
                country: fullForm?.actualplace?.country || formValues?.actualCountry || "FR",
            },
            birthplace: {
                zipcode: fullForm?.birthplace?.zipcode || formValues?.birthZipcode,
                city: fullForm?.birthplace?.city || formValues?.birthCity,
                country: fullForm?.birthplace?.country || formValues?.birthCountry || "FR",
            },
            deathplace: {
                zipcode: fullForm?.deathplace?.zipcode || formValues?.deathZipcode,
                city: fullForm?.deathplace?.city || formValues?.deathCity,
                country: fullForm?.deathplace?.country || formValues?.deathCountry || "FR"
            }
        }

        await createDeceased(token, body)
            .then(response => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    setFullForm()
                    setShowModal(false)
                    if (setDeceasedInfos) {
                        setDeceasedInfos();
                    }
                } else {
                    getErrorListFromValidation(response?.response?.data?.error)
                }
            })
    }

    return (
        <>
                <form id="modification-defunt" onSubmit={handleSubmit} noValidate>
                    <i onClick={() => setShowModal(false)} className="fa fa-times close-button"/>
                    <Icon source={gestionDefunt} span="Modification d'un défunt"/>
                    <fieldset>
                        <div className="portrait-group">
                            <div className="portrait-container portrait-deceased">
                                <label className="portrait-label center">
                                    Portrait du défunt
                                </label>

                                <div className="portrait-frame">
                                    {selectedImage ? (
                                        <img
                                            src={selectedImage}
                                            alt="Image sélectionnée"
                                        />
                                    ) : (!fullForm?.portrait || fullForm?.portrait === "") ? (
                                        // Si portrait est vide, afficher l'image par défaut de l'agence
                                        agencyDefaultDeceasedImage?.defaultImage ? (
                                            <img
                                                src={agencyDefaultDeceasedImage?.isAgencySpecific
                                                    ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                                                    : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`}
                                                alt="Portrait par défaut"
                                            />
                                        ) : (
                                            <span className="portrait-placeholder">
                                            Aucune image
                                        </span>
                                        )
                                    ) : deceasedInfos?.useGalleryPortrait && fullForm?.portrait ? (
                                        <img
                                            src={`${IMG_FOLDER}/img/deceased_library/${agencyId}/${fullForm?.portrait}`}
                                            alt="Portrait de la bibliothèque"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${IMG_FOLDER}/img/deceased_library/${fullForm?.portrait}`;
                                            }}
                                        />
                                    ) : fullForm?.portrait && deceasedInfos?.uploadDir ? (
                                        <img
                                            src={`${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${fullForm?.portrait}`}
                                            alt="Portrait du défunt"
                                        />
                                    ) : (
                                        <span className="portrait-placeholder">
                                        Aucune image
                                    </span>
                                    )}
                                </div>
                            </div>

                            <div className="portrait-actions">
                                <button
                                    type="button"
                                    onClick={() => {
                                        portraitSnapshotRef.current = {
                                            portrait: fullForm?.portrait,
                                            useGalleryPortrait: fullForm?.useGalleryPortrait,
                                            url: fullForm?.url,
                                            file: fullForm?.file,
                                            file_name: fullForm?.file_name,
                                            selectedImage,
                                            selectedPortrait,
                                            selectedCompressedFile,
                                            fileName,
                                            useDefaultPortrait,
                                        };
                                        setShowPortraitModal(true);
                                    }}
                                    className="portrait-modify-btn-form"
                                >
                                    <i className="fa fa-image"></i>
                                    <span>Modifier le portrait</span>
                                </button>

                                <div className="portrait-default-checkbox">
                                    <input
                                        type="checkbox"
                                        id="useDefaultPortrait"
                                        name="useDefaultPortrait"
                                        checked={useDefaultPortrait}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setUseDefaultPortrait(isChecked);

                                            if (isChecked) {
                                                // Utiliser l'image par défaut de l'agence : le fichier vit
                                                // dans deceased_library (useGalleryPortrait doit donc être
                                                // true), pas dans le dossier d'upload du défunt. Envoyer ""
                                                // + false ici forçait le back à deviner/corriger après coup.
                                                const defaultImage = agencyDefaultDeceasedImage?.defaultImage || "";
                                                setFullForm(prevForm => ({
                                                    ...prevForm,
                                                    portrait: defaultImage,
                                                    useGalleryPortrait: !!defaultImage,
                                                    url: "",
                                                    file: null,
                                                    file_name: null
                                                }));

                                                setSelectedPortrait(null);
                                                setSelectedCompressedFile(null);
                                                setFileName("");

                                                // Afficher l'image par défaut directement
                                                if (agencyDefaultDeceasedImage?.defaultImage) {
                                                    setSelectedImage(agencyDefaultDeceasedImage?.isAgencySpecific
                                                        ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                                                        : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`);
                                                } else {
                                                    setSelectedImage(null);
                                                }
                                            } else {
                                                setSelectedImage(null);
                                                setSelectedPortrait(null);
                                                setSelectedCompressedFile(null);
                                                setFileName("");
                                            }
                                        }}
                                    />
                                    <label htmlFor="useDefaultPortrait">Utiliser l'image par défaut</label>
                                </div>
                            </div>
                        </div>
                        <legend>Informations générales</legend>
                        <div className="form-group">
                            <CivilityForm required="required" onChange={onChange} value={fullForm?.civility} />
                            <div className="form-input">
                                <label htmlFor="hiddenCivility">Masquer la civilité <br/><span>(masquer s'il s'agit d'un enfant)</span></label>
                                <input
                                    type="checkbox"
                                    name="hiddenCivility"
                                    defaultChecked={fullForm?.hiddenCivility}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <LastNameForm required={true} onChange={onChange} value={fullForm?.lastName} />
                            { fullForm?.civility !== "M" &&
                                <div className="form-input">
                                    <label htmlFor="birthName">Nom de naissance </label>
                                    <input
                                        placeholder="Nom de naissance"
                                        type="text"
                                        name="birthName"
                                        defaultValue={fullForm?.birthName}
                                        pattern={patternListChoices?.lastname}
                                        onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChange(e); }} />
                                </div>
                            }
                            <FirstnameForm required={true} onChange={onChange} value={fullForm?.firstName} />
                        </div>

                        <InputFullAddress
                            zipField="actualZipcode"
                            cityField="actualCity"
                            addressField="address"
                            compField="addressComp"
                            countryField="actualCountry"
                            address={fullForm?.actualplace?.address}
                            comp={fullForm?.actualplace?.addressComp}
                            city={fullForm?.actualplace?.city}
                            zipcode={fullForm?.actualplace?.zipcode}
                            country={fullForm?.actualplace?.country || "FR"}
                            required={true}
                            onChange={onChangeActualPlace}
                            type="actual"
                        />
                    </fieldset>
                    <FormBirthInfos
                        fullForm={fullForm}
                        setFullForm={setFullForm}
                    />
                    <FormDeathInfos
                        fullForm={fullForm}
                        setFullForm={setFullForm}
                    />
                    <div className="publication-internet">
                        <label className="required" htmlFor="isListed">
                            Autoriser la publication de la page du défunt sur Internet ?
                        </label>
                        <div className="publication-options">
                            <div className="publication-option">
                                <input
                                    type="radio"
                                    id="isListed1"
                                    name="isListed"
                                    value={1}
                                    required
                                    defaultChecked={fullForm?.isListed == 1}
                                    onChange={handlePublicationChange}
                                />
                                <label htmlFor="isListed1">Oui</label>
                            </div>
                            <div className="publication-option">
                                <input
                                    type="radio"
                                    id="isListed0"
                                    name="isListed"
                                    value={0}
                                    defaultChecked={fullForm?.isListed == 0}
                                    onChange={handlePublicationChange}
                                />
                                <label htmlFor="isListed0">Non</label>
                            </div>
                        </div>
                    </div>
                    <div className="errors"></div>
                    <div className="buttons-container">
                        <button type="button" onClick={() => setShowModal(false)} className="red-button">
                            <i className="fa fa-xmark"></i>
                            <span>Annuler</span>
                        </button>
                        <button type="submit" className="green-button">
                            <i className="fa fa-check"></i>
                            <span>Valider les modifications</span>
                        </button>
                    </div>
                </form>

        {showPortraitModal && (
            <div className="modal-overlay modal-overlay-secondary">
                <div className="modal-content modal-portrait-unified" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>
                            <i className="fa fa-image"></i>
                            Modifier le portrait
                        </h3>
                        <i className="fa fa-times close-button" onClick={handleCancelPortrait}></i>
                    </div>
                    <div className="modal-body modal-body-portrait">
                        {/* Section gauche: Bibliothèque */}
                        <div className="portrait-section portrait-library-section">
                            <h4>
                                <i className="fa fa-images"></i>
                                Bibliothèque d'images
                            </h4>
                            <div className="library-images-container">
                                <div className="library-images-grid">
                                    {combinedImageList?.map((image) => (
                                        <div
                                            key={image.uniqueKey}
                                            className={`library-image-item ${selectedPortrait === image?.url ? "selected" : ""}`}
                                            onClick={() => handleLibFileSelected(image?.url, image?.path)}
                                        >
                                            {selectedPortrait === image?.url && (
                                                <i className="fa fa-check-circle" />
                                            )}
                                            <img
                                                src={image.path}
                                                alt="Portrait du défunt"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {combinedImageList?.length === 0 && (
                                    <p className="no-images">Aucune image disponible dans la bibliothèque</p>
                                )}
                            </div>
                        </div>

                        {/* Section droite: Import */}
                        <div className="portrait-section portrait-upload-section">
                            <h4>
                                <i className="fa fa-upload"></i>
                                Importer une image
                            </h4>
                            <div className="upload-content">
                                {!selectedImage ? (
                                    <label htmlFor="portrait" className="upload-label">
                                        <i className="fa fa-cloud-upload-alt"></i>
                                        <span>Sélectionner une image</span>
                                    </label>
                                ) : (
                                    <div className="upload-preview-compact">
                                        <img src={selectedImage} alt="Aperçu" />
                                        <div className="preview-info">
                                            <i className="fa fa-check-circle"></i>
                                            <p className="file-name">{fileName}</p>
                                            <button
                                                type="button"
                                                className="change-image-btn"
                                                onClick={() => {
                                                    setSelectedImage(null);
                                                    setSelectedCompressedFile(null);
                                                    setFileName("");
                                                }}
                                            >
                                                <i className="fa fa-sync-alt"></i>
                                                Changer
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    name="portrait"
                                    accept="image/*"
                                    id="portrait"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                            </div>
                            <div className="portrait-upload-actions">
                                <button type="button" className="red-button" onClick={handleCancelPortrait}>
                                    <i className="fa fa-times"></i>
                                    <span>Annuler</span>
                                </button>
                                <button type="button" className="green-button" onClick={() => setShowPortraitModal(false)}>
                                    <i className="fa fa-check"></i>
                                    <span>Valider</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    )
}